import React, { useCallback, useId } from 'react';

import { di } from 'react-magnetic-di';
import {
	type Action,
	createContainer,
	createHook,
	createStore,
	defaultRegistry,
} from 'react-sweet-state';

import {
	getCurrentConversation,
	hide,
	onClose,
	onOpen,
} from '@atlassian/customer-service-management-widget-sdk-react/commands';
import { CSM } from '@atlassian/customer-service-management-widget-sdk-react/csm';

import { initCSM } from '../../utils/initCSM';

type Id = string;

interface State {
	inputValue: string;
	observer: IntersectionObserver | null;
	triggers: Record<
		Id,
		{
			element: HTMLElement;
			isIntersecting: boolean | null;
		}
	>;
	/**
	 * Whether the CSM widget is visible or not (launcher/bubble is visible)
	 * Not the same as open state
	 */
	widgetVisibility: 'hidden' | 'visible' | null;
}

const actions = {
	resetWidgetVisibility:
		(): Action<State> =>
		({ setState }) => {
			setState({ widgetVisibility: 'hidden' });
		},
	setInputValue:
		(inputValue: string): Action<State> =>
		({ setState }) => {
			setState({ inputValue });
		},
	observe:
		(id: string, element: HTMLElement): Action<State> =>
		({ setState, getState }) => {
			const { triggers, observer: currentObserver } = getState();

			const observer =
				currentObserver ??
				new IntersectionObserver(
					([entry]) => {
						setState({
							triggers: { ...triggers, [id]: { element, isIntersecting: entry.isIntersecting } },
						});
					},
					{ threshold: 0 },
				);

			setState({
				observer,
			});

			observer.observe(element);
		},
	disconnect:
		(id: string): Action<State> =>
		({ setState, getState }) => {
			const { triggers: prevTriggers, observer } = getState();

			if (observer && prevTriggers[id]) {
				observer.unobserve(prevTriggers[id].element);
			}

			const triggers = { ...prevTriggers };

			delete triggers[id];

			setState({
				triggers,
			});
		},
};

type Actions = typeof actions;
type ContainerProps = { locale?: string; site?: string; siteId?: string; widgetId?: string };

const StoreContainer = createContainer<ContainerProps>();

export const AiSearchTriggerContainer = ({
	children,
	...props
}: ContainerProps & { children: React.ReactNode }): React.JSX.Element => {
	return (
		<StoreContainer {...props} isGlobal>
			{children}
		</StoreContainer>
	);
};

const store = createStore<State, Actions, ContainerProps>({
	name: 'csm-ai-search-trigger-store',
	initialState: {
		inputValue: '',
		widgetVisibility: null,
		triggers: {},
		observer: null,
	},
	actions,
	containedBy: StoreContainer,
	handlers: {
		onInit:
			() =>
			({ setState }, { locale, site, siteId, widgetId }: ContainerProps) => {
				di(CSM);

				initCSM({ locale, site, siteId, widgetId });

				hide();

				setState({ widgetVisibility: 'hidden' });

				onOpen(() => {
					setState({ widgetVisibility: 'visible' });
				});

				onClose(() => {
					getCurrentConversation((conversation) => {
						if (!conversation) {
							hide();
							setState({ widgetVisibility: 'hidden' });
						}
					});
				});
			},
	},
});

/** SDK hide() does not emit onClose; reset the existing global store without initializing the SDK. */
export const resetSearchTriggerVisibility = (): void => {
	if (defaultRegistry.hasStore(store)) {
		defaultRegistry.getStore(store).actions.resetWidgetVisibility();
	}
};

const searchFieldValueSelector = (state: State) => state.inputValue;

const useSearchFieldValueHook = createHook(store, {
	selector: searchFieldValueSelector,
});

/**
 * Read and write the search field value
 */
export const useAiSearch = (): {
	onChange: (value: string) => void | Promise<void>;
	value: string;
} => {
	const [inputValue, { setInputValue }] = useSearchFieldValueHook();

	return {
		value: inputValue,
		onChange: (value: string): void | Promise<void> => setInputValue(value),
	};
};

const useAiSearchStore = createHook(store);

/**
 * Connect a search trigger to be observed
 */
export const useTriggerIntersectionObserver = (): {
	ref: (element: HTMLElement | null) => void;
} => {
	const id = useId();
	const [_, { observe, disconnect }] = useAiSearchStore();

	const ref = useCallback(
		(element: HTMLElement | null): void => {
			if (element) {
				observe(id, element);
			} else {
				disconnect(id);
			}
		},
		[observe, disconnect, id],
	);

	return {
		ref,
	};
};

const hasIntersectingTriggerSelector = (state: State) => {
	return Object.values(state.triggers).some((trigger) => trigger.isIntersecting);
};

const useHasIntersectingTriggerHook = createHook(store, {
	selector: hasIntersectingTriggerSelector,
});

/**
 * Determine if any tracked trigger is intersecting the viewport
 * @returns true if any tracked trigger is intersecting the viewport
 */
export const useHasIntersectingTrigger = (): boolean => {
	const [hasIntersectingTrigger] = useHasIntersectingTriggerHook();

	return hasIntersectingTrigger;
};

/**
 * Exposes whether the search triggers should be visible or not
 */
export const useSearchTriggerVisibility = (): {
	isTriggerVisible: boolean;
} => {
	const [{ widgetVisibility }] = useAiSearchStore();

	return {
		isTriggerVisible: widgetVisibility !== 'visible',
	};
};
